import React, { useEffect, useMemo, useState, KeyboardEvent, ChangeEvent, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Row } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
    DesktopTableLoader,
    MobileCardsLoader,
    DataTableStatus,
} from "components/DataTableLoader";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    createColumnHelper,
} from "@tanstack/react-table";
import { getImageSrc, usePresignedMap } from "components/Tableimageshelpers";
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import { getAuthCookie } from "../../utils/authCookies";
import { showError, showSuccess } from "toast/toast";
import { EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import {
    deleteEmailTemplate,
    fetchEmailTemplatesDatatable,
    hardDeleteEmailTemplateStorageFile,
    type EmailTemplatesDatatableRow,
} from "components/emailtempleateapicalls";

export type EmailTemplateListRow = {
    id?: string;
    _id?: string;
    /** file id or URL for logo */
    logo?: string;
    company_logo?: string;
    company_logo_presigned_url?: string;
    images?: { url?: string };
    message?: string;
    customer_message?: string;
    custom_message?: string;
    is_default?: boolean;
    default?: boolean;
};

/** Synthetic field for presigned logo lookup */
type EmailTemplateRowWithImage = EmailTemplateListRow & { image?: string };

function fuzzyTextFilter<TData>(row: Row<TData>, columnId: string, filterValue: string) {
    const raw = row.getValue(columnId);
    const value = String(raw ?? "").toLowerCase();
    return value.includes(filterValue.toLowerCase());
}

function pickLogo(row: EmailTemplateListRow): string | undefined {
    return row.company_logo_presigned_url || row.company_logo || row.logo || row.images?.url;
}

function pickMessage(row: EmailTemplateListRow): string {
    return (row.message || row.customer_message || row.custom_message || "").trim();
}

function pickId(row: EmailTemplateListRow): string {
    return String(row._id || row.id || "");
}

function pickDefault(row: EmailTemplateListRow): boolean {
    if (typeof row.is_default === "boolean") return row.is_default;
    if (typeof row.default === "boolean") return row.default;
    return false;
}

const LIST_TEXT_MAX_LEN = 40;

function truncateListText(text: string, maxLen = LIST_TEXT_MAX_LEN): { display: string; full: string } {
    const full = (text || "").trim();
    if (!full) return { display: "", full: "" };
    if (full.length <= maxLen) return { display: full, full };
    return { display: `${full.slice(0, maxLen)}…`, full };
}

/** Shown when `/emailteamplets` / datatable is unavailable or returns empty */
// const DEFAULT_EMAIL_TEMPLATE_ROWS: EmailTemplateListRow[] = [
//     {
//         _id: "demo-email-1",
//         message: "Thank you for your business. We look forward to serving you again.",
//         is_default: true,
//     },
//     {
//         _id: "demo-email-2",
//         message: "We appreciate you choosing our signage services.",
//         is_default: false,
//     },
// ];

const columnHelper = createColumnHelper<EmailTemplateListRow>();

const SignageemailTeampleates = () => {
    const [rows, setRows] = useState<EmailTemplateListRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [usingFallback, setUsingFallback] = useState(false);
    const [deletingId, setDeletingId] = useState<string>("");

    const token = getAuthCookie("sessionToken");
    const location = useLocation();
    const isSignageTeammember = location.pathname.includes("/signageteammember");
    const baseRoute = isSignageTeammember ? "/signageteammember" : "/signage";

    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>("");
    const [refreshKey, setRefreshKey] = useState(0);

    const normalizeDatatableRows = (list: EmailTemplatesDatatableRow[]): EmailTemplateListRow[] =>
        list.map((row) => ({
            ...row,
            _id: row._id || row.id,
            company_logo: row.company_logo,
            company_logo_presigned_url: row.company_logo_presigned_url,
            customer_message: row.customer_message ?? row.message ?? row.custom_message ?? "",
            is_default: typeof row.is_default === "boolean" ? row.is_default : row.default,
            default: typeof row.default === "boolean" ? row.default : row.is_default,
        }));

    const handleDeleteTemplate = useCallback(
        async (row: EmailTemplateListRow) => {
            const id = pickId(row);
            if (!id || !token) return;
            const ok = window.confirm("Are you sure you want to delete this email template?");
            if (!ok) return;
            setDeletingId(id);
            try {
                await deleteEmailTemplate(token, id);
                const logoId = String(row.company_logo || "").trim();
                if (logoId) {
                    try {
                        await hardDeleteEmailTemplateStorageFile(token, logoId);
                    } catch {
                        // ignore logo delete failure; template is already deleted
                    }
                }
                showSuccess("Email template deleted successfully.");
                setRefreshKey((k) => k + 1);
            } catch (error) {
                showError(error instanceof Error ? error.message : "Failed to delete email template.");
            } finally {
                setDeletingId("");
            }
        },
        [token]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                let list: EmailTemplateListRow[] = [];
                let total = 0;

                if (token) {
                    const datatable = await fetchEmailTemplatesDatatable(token, {
                        page: pagination.pageIndex + 1,
                        limit: pagination.pageSize,
                        search: globalFilter,
                        filters: {},
                        sort: { field: "created_at", order: "desc" },
                    });
                    list = normalizeDatatableRows(datatable.rows);
                    total = datatable.total;
                }

                if (cancelled) return;

                if (!list.length) {
                    // setRows(DEFAULT_EMAIL_TEMPLATE_ROWS);
                    // setRowCount(DEFAULT_EMAIL_TEMPLATE_ROWS.length);
                    setUsingFallback(true);
                } else {
                    setRows(list);
                    setRowCount(total || list.length);
                    setUsingFallback(false);
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) {
                    // setRows(DEFAULT_EMAIL_TEMPLATE_ROWS);
                    // setRowCount(DEFAULT_EMAIL_TEMPLATE_ROWS.length);
                    setUsingFallback(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [token, globalFilter, pagination.pageIndex, pagination.pageSize, refreshKey]);

    const rowsForPresign = useMemo(
        () => rows.map((r) => ({ ...r, image: r.company_logo || r.logo || "" })),
        [rows]
    );
    const imageUrlMap = usePresignedMap(rowsForPresign as EmailTemplateRowWithImage[], "image", token);
    const logoCell = useCallback(
        (info: any) => {
            const original = info.row.original as EmailTemplateListRow;
            const directPresigned = String(original.company_logo_presigned_url || "").trim();
            const raw = String(original.company_logo || original.logo || original.images?.url || "").trim();
            const resolved =
                /^https?:\/\//i.test(directPresigned)
                    ? directPresigned
                    : getImageSrc(raw, imageUrlMap);
            const src = resolved || imagecustomRounded;
            return (
                <div className="text-sm text-center S-No-2">
                    <img
                        src={src}
                        alt="Company logo"
                        style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }}
                        onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                        }}
                    />
                </div>
            );
        },
        [imageUrlMap]
    );

    const columns = useMemo(() => {
        return [
            columnHelper.display({
                id: "serial",
                header: () => <div className="th-text text-center S-No">S.No.</div>,
                cell: (info) => (
                    <div className="text-sm text-center S-No">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
                enableGlobalFilter: false,
            }),
            columnHelper.display({
                id: "company_logo",
                header: () => <div className="th-text text-center S-No-2">Company Logo</div>,
                cell: logoCell,
                enableGlobalFilter: false,
            }),
            columnHelper.accessor((row) => pickMessage(row), {
                id: "message",
                header: () => <div className="th-text text-center">Message</div>,
                cell: (info) => {
                    const text = info.getValue() as string;
                    const { display, full } = truncateListText(text);
                    return (
                        <div className="text-sm text-center" title={full || undefined}>
                            {display || <span className="text-muted">—</span>}
                        </div>
                    );
                },
                filterFn: fuzzyTextFilter,
                enableGlobalFilter: true,
            }),
            columnHelper.display({
                id: "default",
                header: () => <div className="th-text text-center">Default</div>,
                cell: (info) => {
                    const isDefault = pickDefault(info.row.original);
                    return (
                        <div className="text-sm text-center">
                            <span
                                className={`badge rounded-pill px-3 py-2 ${
                                    isDefault
                                        ? "bg-success-subtle text-success-emphasis"
                                        : "bg-body-tertiary text-secondary-emphasis"
                                }`}
                            >
                                {isDefault ? "Yes" : "No"}
                            </span>
                        </div>
                    );
                },
                enableGlobalFilter: false,
            }),
            columnHelper.display({
                id: "actions",
                header: () => <div className="th-text">Actions</div>,
                cell: (info) => {
                    const row = info.row.original;
                    const id = pickId(row);
                    if (!id || id.startsWith("demo-email-")) {
                        return <div className="text-sm text-muted">—</div>;
                    }
                    return (
                        <div className="d-flex align-items-center gap-2">
                            <EditIconButton
                                to={`${baseRoute}/Signageemailteampleate?templateId=${encodeURIComponent(id)}`}
                                className="icon-square"
                                title="Edit Template"
                            />
                            <DeleteIconButton
                                title="Delete Template"
                                className="icon-square danger"
                                onClick={() => handleDeleteTemplate(row)}
                                disabled={deletingId === id}
                            />
                        </div>
                    );
                },
                enableGlobalFilter: false,
            }),
        ];
    }, [pagination, logoCell, baseRoute, handleDeleteTemplate, deletingId]);

    const serverMode = !usingFallback;
    const table = useReactTable({
        data: rows,
        columns,
        pageCount: serverMode ? Math.max(1, Math.ceil(rowCount / pagination.pageSize)) : undefined,
        state: {
            pagination,
            globalFilter,
        },
        manualPagination: serverMode,
        manualFiltering: serverMode,
        onPaginationChange: setPagination,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: usingFallback ? getPaginationRowModel() : undefined,
    });

    return (
        <div className="content container">
            <div className="po-header mb-4 shadow-border-datatables ">
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2 d-flex align-items-center">
                        <h4 className="po-title mb-0">Email Templates</h4>
                    </div>

                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        <div className="input-group po-search ">
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                type="text"
                                id="searchInput"
                                name="searchInput"
                                className="form-control"
                                placeholder="Search by Message"
                                value={searchInput}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === "Enter") {
                                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                                        setGlobalFilter(searchInput);
                                    }
                                }}
                                autoComplete="off"
                            />
                        </div>
                        <Link to={`${baseRoute}/Signageemailteampleate`} className="btn btn-primary  table-addbutton">
                            + Email Template
                        </Link>
                    </div>
                </div>
            </div>

            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
                    <h5 className="mb-0 "></h5>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />

                <div className="rdt-desktop ">
                    <div className="production-scroll" style={{ "--rdt-min": "1200px" } as React.CSSProperties}>
                        <table className="table production-table align-middle mb-0 table-1">
                            {loading ? (
                                <DesktopTableLoader table={table} message="Loading email templates…" />
                            ) : (
                                <DesktopTableParts table={table} />
                            )}
                        </table>
                    </div>
                </div>

                <div className="rdt-mobile">
                    {loading ? (
                        <MobileCardsLoader message="Loading email templates…" />
                    ) : (
                        <MobileCardsOnly
                            table={table}
                            idColId="serial"
                            titleColId="message"
                            actionColId="actions"
                            omitMobile={[]}
                        />
                    )}
                </div>
            </div>

            {usingFallback && (
                <div className="small text-muted mt-2 px-1">
                    Showing sample rows (API unavailable or empty).
                </div>
            )}

            <DataTableStatus
                loading={loading}
                isEmpty={table.getRowModel().rows.length === 0}
            >
                <div className="mt-4 mb-4 ">
                    <TablePaginationControls table={table} />
                </div>
            </DataTableStatus>
        </div>
    );
};

export default SignageemailTeampleates;
